import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { productosAPI, clientesAPI, cotizacionesAPI } from '../api/client';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { Search, Plus, Trash2, Save, User, ArrowLeft, X, FileText, MessageCircle, Package, DollarSign, Tag } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const NuevaCotizacionPage = () => {
  const navigate = useNavigate();
  const [productos, setProductos] = useState([]);
  const [carrito, setCarrito] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  
  // Cliente state
  const [clienteId, setClienteId] = useState(null);
  const [clienteNombre, setClienteNombre] = useState('');
  const [clienteTelefono, setClienteTelefono] = useState('');
  
  const [clientes, setClientes] = useState([]);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [showSelectorCliente, setShowSelectorCliente] = useState(false);
  const [showModalPersonalizado, setShowModalPersonalizado] = useState(false);
  const [itemPersonalizado, setItemPersonalizado] = useState({
    nombre: '',
    precio: '',
    cantidad: 1
  });
  const [tipoPrecios, setTipoPrecios] = useState('normal'); // 'normal' o 'mayorista'
  
  const searchInputRef = useRef(null);
  const { formatCurrency, config } = useConfig();
  const { user } = useAuth();

  useEffect(() => {
    loadProductos();
    loadClientes();
  }, []);

  const loadProductos = async () => {
    try {
      const response = await productosAPI.getAll();
      if (response.data.success) {
        setProductos(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
    }
  };

  const loadClientes = async () => {
    try {
      const response = await clientesAPI.getAll();
      if (response.data.success) {
        setClientes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  const buscarProducto = async () => {
    if (!busqueda) return;
    const term = busqueda.toLowerCase();
    const producto = productos.find(p => 
      p.codigo_barras === busqueda || 
      (p.sku && p.sku === busqueda) ||
      p.nombre.toLowerCase().includes(term)
    );

    if (producto) {
      agregarAlCarrito(producto);
      setBusqueda('');
      if (searchInputRef.current) searchInputRef.current.focus();
    } else {
      alert('Producto no encontrado');
    }
  };

  const agregarAlCarrito = (producto, esPersonalizado = false) => {
    // Para items personalizados, usar un ID único negativo
    const itemId = esPersonalizado ? -Date.now() : producto.id;
    const existe = carrito.find(item => item.id === itemId);
    
    if (existe && !esPersonalizado) {
      setCarrito(carrito.map(item =>
        item.id === itemId
          ? { ...item, cantidad: item.cantidad + 1 }
          : item
      ));
    } else {
      // Determinar el precio según el tipo seleccionado
      let precioUnitario = producto.precio_venta;
      if (!esPersonalizado && tipoPrecios === 'mayorista' && producto.precio_mayoreo) {
        precioUnitario = producto.precio_mayoreo;
      }
      
      setCarrito([...carrito, {
        ...producto,
        id: itemId,
        cantidad: producto.cantidad || 1,
        precio_unitario: esPersonalizado ? producto.precio : precioUnitario,
        descuento: 0,
        es_personalizado: esPersonalizado
      }]);
    }
  };

  const agregarItemPersonalizado = () => {
    if (!itemPersonalizado.nombre.trim()) {
      alert('Por favor ingrese el nombre del item');
      return;
    }
    if (!itemPersonalizado.precio || parseFloat(itemPersonalizado.precio) <= 0) {
      alert('Por favor ingrese un precio válido');
      return;
    }

    const nuevoItem = {
      nombre: itemPersonalizado.nombre.trim(),
      precio: parseFloat(itemPersonalizado.precio),
      cantidad: parseFloat(itemPersonalizado.cantidad) || 1,
      sku: 'PERSONALIZADO',
      codigo_barras: ''
    };

    agregarAlCarrito(nuevoItem, true);
    
    // Limpiar y cerrar modal
    setItemPersonalizado({ nombre: '', precio: '', cantidad: 1 });
    setShowModalPersonalizado(false);
  };

  const eliminarDelCarrito = (id) => {
    setCarrito(carrito.filter(item => item.id !== id));
  };

  const actualizarCantidad = (id, cantidad) => {
    if (cantidad <= 0) {
      eliminarDelCarrito(id);
      return;
    }
    setCarrito(carrito.map(item =>
      item.id === id ? { ...item, cantidad: parseFloat(cantidad) } : item
    ));
  };

  const actualizarDescuento = (id, descuento) => {
    setCarrito(carrito.map(item =>
      item.id === id ? { ...item, descuento: parseFloat(descuento) || 0 } : item
    ));
  };

  const calcularTotal = () => {
    return carrito.reduce((sum, item) => {
      const subtotal = item.precio_unitario * item.cantidad;
      return sum + (subtotal - (item.descuento || 0));
    }, 0);
  };

  const seleccionarCliente = (cliente) => {
    setClienteId(cliente.id);
    setClienteNombre(cliente.nombre);
    setClienteTelefono(cliente.telefono || '');
    setShowSelectorCliente(false);
  };

  const guardarCotizacion = async () => {
    // Validaciones
    if (carrito.length === 0) {
      alert('⚠️ El carrito está vacío. Agrega productos para continuar.');
      return;
    }

    if (!clienteNombre.trim()) {
      if (!window.confirm('¿Desea guardar la cotización sin nombre de cliente?')) {
        return;
      }
    }

    // Preparar datos
    const cotizacion = {
      cliente_id: clienteId || null,
      cliente_nombre: clienteNombre.trim(),
      cliente_telefono: clienteTelefono.trim(),
      items: carrito.map(item => ({
        producto_id: item.es_personalizado ? null : item.id,
        nombre_personalizado: item.es_personalizado ? item.nombre : null,
        cantidad: parseFloat(item.cantidad),
        precio: parseFloat(item.precio_unitario),
        descuento: parseFloat(item.descuento || 0),
        es_personalizado: item.es_personalizado || false
      })),
      tipo_precios: tipoPrecios,
      total: parseFloat(calcularTotal().toFixed(2)),
      descuento: 0,
      fecha: new Date().toISOString(),
      vigencia: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    };

    try {
      const response = await cotizacionesAPI.create(cotizacion);
      
      if (response.data.success) {
        const folio = response.data.data?.folio || 'N/A';
        alert(`✅ Cotización ${folio} guardada exitosamente`);
        
        // Limpiar formulario
        setCarrito([]);
        setClienteId(null);
        setClienteNombre('');
        setClienteTelefono('');
        
        navigate('/cotizaciones');
      } else {
        alert('❌ Error: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error al guardar cotización:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error de conexión';
      alert(`❌ Error al guardar: ${errorMsg}`);
    }
  };

  const compartirWhatsApp = () => {
    if (carrito.length === 0) {
      alert('⚠️ Agrega productos primero');
      return;
    }

    const nombreNegocio = config?.nombre_negocio || 'Nuestro Negocio';
    const nombre = clienteNombre.trim() || 'Cliente';
    
    let mensaje = `*${nombreNegocio.toUpperCase()}*\n`;
    mensaje += `📋 *COTIZACIÓN*\n`;
    mensaje += `━━━━━━━━━━━━━━━━\n\n`;
    mensaje += `Hola *${nombre}*, aquí está tu cotización:\n\n`;
    
    carrito.forEach((item, index) => {
      const subtotal = (item.precio_unitario * item.cantidad) - (item.descuento || 0);
      mensaje += `${index + 1}. *${item.nombre}*\n`;
      mensaje += `   ${item.cantidad} x ${formatCurrency(item.precio_unitario)}`;
      if (item.descuento > 0) {
        mensaje += ` - ${formatCurrency(item.descuento)}`;
      }
      mensaje += ` = ${formatCurrency(subtotal)}\n\n`;
    });

    mensaje += `━━━━━━━━━━━━━━━━\n`;
    mensaje += `💰 *TOTAL: ${formatCurrency(calcularTotal())}*\n`;
    mensaje += `\n📅 Vigencia: 7 días\n`;
    mensaje += `\n¡Gracias por tu preferencia! 🙏`;
    
    const numero = clienteTelefono ? clienteTelefono.replace(/[^0-9]/g, '') : '';
    const url = numero 
      ? `https://wa.me/${numero}?text=${encodeURIComponent(mensaje)}`
      : `https://wa.me/?text=${encodeURIComponent(mensaje)}`;
    
    window.open(url, '_blank');
  };

  const descargarPDF = () => {
    if (carrito.length === 0) {
      alert('⚠️ No hay productos en el carrito');
      return;
    }

    try {
      const doc = new jsPDF();
      const nombreNegocio = config?.nombre_negocio || 'Mi Negocio';
      const direccion = config?.direccion || '';
      const telefono = config?.telefono || '';

      // Header con logo (si existe)
      doc.setFontSize(22);
      doc.setFont(undefined, 'bold');
      doc.text(nombreNegocio.toUpperCase(), 105, 20, { align: 'center' });
      
      if (direccion || telefono) {
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        let infoY = 27;
        if (direccion) {
          doc.text(direccion, 105, infoY, { align: 'center' });
          infoY += 4;
        }
        if (telefono) {
          doc.text(`Tel: ${telefono}`, 105, infoY, { align: 'center' });
        }
      }

      // Título
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('COTIZACIÓN', 105, 40, { align: 'center' });

      // Info
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Fecha: ${new Date().toLocaleDateString('es-GT', { 
        year: 'numeric', month: 'long', day: 'numeric' 
      })}`, 20, 50);
      doc.text(`Cliente: ${clienteNombre.trim() || 'Público General'}`, 20, 56);
      if (clienteTelefono.trim()) {
        doc.text(`Teléfono: ${clienteTelefono}`, 20, 62);
      }
      doc.text(`Vigencia: 7 días`, 20, clienteTelefono.trim() ? 68 : 62);

      // Tabla
      const tableData = carrito.map((item, index) => {
        const subtotal = (item.cantidad * item.precio_unitario) - (item.descuento || 0);
        return [
          index + 1,
          item.cantidad,
          item.nombre,
          formatCurrency(item.precio_unitario),
          item.descuento > 0 ? formatCurrency(item.descuento) : '-',
          formatCurrency(subtotal)
        ];
      });

      doc.autoTable({
        startY: clienteTelefono.trim() ? 75 : 69,
        head: [['#', 'Cant', 'Producto', 'Precio', 'Desc.', 'Subtotal']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [41, 128, 185], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 15 },
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right', fontStyle: 'bold' }
        }
      });

      // Total
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(`TOTAL: ${formatCurrency(calcularTotal())}`, 200, finalY, { align: 'right' });

      // Footer
      doc.setFontSize(8);
      doc.setFont(undefined, 'italic');
      doc.text('Gracias por su preferencia', 105, 280, { align: 'center' });

      // Guardar
      const fileName = `cotizacion_${new Date().getTime()}.pdf`;
      doc.save(fileName);
      
      alert('✅ PDF descargado correctamente');
    } catch (error) {
      console.error('Error al generar PDF:', error);
      alert('❌ Error al generar el PDF');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      <div className="bg-white shadow-sm border-b sticky top-0 z-10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/cotizaciones')} className="p-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Nueva Cotización</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={compartirWhatsApp}
              className="btn-secondary flex items-center gap-2 text-green-600 border-green-600 hover:bg-green-50"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="hidden sm:inline">WhatsApp</span>
            </button>
            <button
              onClick={descargarPDF}
              className="btn-secondary flex items-center gap-2"
            >
              <FileText className="w-5 h-5" />
              <span className="hidden sm:inline">PDF</span>
            </button>
            <button
              onClick={guardarCotizacion}
              className="btn-primary flex items-center gap-2"
            >
              <Save className="w-5 h-5" />
              Guardar
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Buscador y controles */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && buscarProducto()}
                    placeholder="Buscar por código, SKU o nombre..."
                    className="input-field pl-10"
                    autoFocus
                  />
                </div>
                <button onClick={buscarProducto} className="btn-primary px-4">
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              
              {/* Controles adicionales */}
              <div className="flex justify-between items-center">
                <button
                  onClick={() => setShowModalPersonalizado(true)}
                  className="btn-secondary flex items-center gap-2 text-sm"
                >
                  <Package className="w-4 h-4" />
                  Item Personalizado
                </button>
                
                {/* Toggle para tipo de precios */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Precios:</label>
                  <select
                    value={tipoPrecios}
                    onChange={(e) => setTipoPrecios(e.target.value)}
                    className="px-3 py-1 border rounded-lg text-sm"
                  >
                    <option value="normal">Normal</option>
                    <option value="mayorista">Mayorista</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Lista de productos */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="font-semibold mb-4">Productos Disponibles</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                {productos.map((producto) => (
                  <button
                    key={producto.id}
                    onClick={() => {
                      agregarAlCarrito(producto);
                      if (searchInputRef.current) searchInputRef.current.focus();
                    }}
                    className="p-3 border rounded-lg hover:bg-gray-50 text-left"
                  >
                    <p className="font-medium truncate">{producto.nombre}</p>
                    <p className="text-xs text-gray-500 mb-1">{producto.sku ? `SKU: ${producto.sku}` : ''}</p>
                    <div className="flex flex-col">
                      <p className="text-primary-600 font-bold">
                        {formatCurrency(tipoPrecios === 'mayorista' && producto.precio_mayoreo ? producto.precio_mayoreo : producto.precio_venta)}
                      </p>
                      {tipoPrecios === 'mayorista' && producto.precio_mayoreo && (
                        <p className="text-xs text-green-600">Mayorista</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Cliente */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Datos del Cliente
                </h3>
                <button 
                    onClick={() => setShowSelectorCliente(true)}
                    className="text-primary-600 text-sm font-medium hover:underline"
                >
                    Buscar Existente
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                    <input
                        type="text"
                        value={clienteNombre}
                        onChange={(e) => {
                            setClienteNombre(e.target.value);
                            if (clienteId) setClienteId(null); // Clear ID if manually editing
                        }}
                        className="input-field w-full"
                        placeholder="Nombre del cliente"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono / WhatsApp</label>
                    <input
                        type="text"
                        value={clienteTelefono}
                        onChange={(e) => setClienteTelefono(e.target.value)}
                        className="input-field w-full"
                        placeholder="Número de teléfono"
                    />
                </div>
              </div>
            </div>

            {/* Carrito */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="font-semibold mb-4">Items ({carrito.length})</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {carrito.map((item) => (
                  <div key={item.id} className="p-3 bg-gray-50 rounded border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.nombre}</p>
                        {item.es_personalizado && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            <Tag className="w-3 h-3 mr-1" />
                            Personalizado
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => eliminarDelCarrito(item.id)}
                        className="text-red-600 hover:bg-red-100 p-1 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="text-xs text-gray-500">Cantidad</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={item.cantidad}
                            onChange={(e) => actualizarCantidad(item.id, e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                            min="1"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Descuento ($)</label>
                        <input
                          type="number"
                          value={item.descuento || 0}
                          onChange={(e) => actualizarDescuento(item.id, e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm text-right"
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">
                        {formatCurrency(item.precio_unitario)} x {item.cantidad}
                      </span>
                      <span className="font-bold text-primary-600">
                        {formatCurrency((item.precio_unitario * item.cantidad) - (item.descuento || 0))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 pt-4 border-t">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-primary-600">{formatCurrency(calcularTotal())}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Item Personalizado */}
      {showModalPersonalizado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Package className="w-5 h-5" />
                Agregar Item Personalizado
              </h2>
              <button onClick={() => setShowModalPersonalizado(false)}>
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Item *
                </label>
                <input
                  type="text"
                  value={itemPersonalizado.nombre}
                  onChange={(e) => setItemPersonalizado({...itemPersonalizado, nombre: e.target.value})}
                  className="input-field w-full"
                  placeholder="Ej: Servicio de instalación"
                  autoFocus
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio Unitario *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="number"
                      value={itemPersonalizado.precio}
                      onChange={(e) => setItemPersonalizado({...itemPersonalizado, precio: e.target.value})}
                      className="input-field w-full pl-8"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cantidad
                  </label>
                  <input
                    type="number"
                    value={itemPersonalizado.cantidad}
                    onChange={(e) => setItemPersonalizado({...itemPersonalizado, cantidad: e.target.value})}
                    className="input-field w-full"
                    placeholder="1"
                    min="1"
                  />
                </div>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Subtotal:</p>
                <p className="text-lg font-bold text-primary-600">
                  {formatCurrency((parseFloat(itemPersonalizado.precio) || 0) * (parseFloat(itemPersonalizado.cantidad) || 1))}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowModalPersonalizado(false)}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={agregarItemPersonalizado}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Agregar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Selector Cliente */}
      {showSelectorCliente && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Seleccionar Cliente</h2>
              <button onClick={() => setShowSelectorCliente(false)}>
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>
            <input
              type="text"
              value={busquedaCliente}
              onChange={(e) => setBusquedaCliente(e.target.value)}
              placeholder="Buscar cliente..."
              className="input-field mb-4"
            />
            <div className="max-h-60 overflow-y-auto">
              {clientes.filter(c => 
                c.nombre.toLowerCase().includes(busquedaCliente.toLowerCase())
              ).map(c => (
                <button
                  key={c.id}
                  onClick={() => seleccionarCliente(c)}
                  className="w-full text-left p-3 hover:bg-gray-50 border-b"
                >
                  <p className="font-medium">{c.nombre}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NuevaCotizacionPage;
